<script>
import { permissionMixin } from "@/mixins/permissions";
import { getFileNameFromResponseHeader } from "@/utils";
import { handleError } from "@/utils/error";
import util from "@/modules/data-path/util";

export default {
  mixins: [permissionMixin],
  data() {
    return {
      valid: false,
      loading: false,
      certificationTypes: [
        {
          text: "Client",
          value: 1
        },
        {
          text: "Server",
          value: 2
        }
      ],
      util,
      certTypeRules: [
        v =>
          !!v ||
          this.$t("modules.dataPath.vpn.certificationType.error.required")
      ],
      certCNRules: [
        v =>
          !!v || this.$t("modules.dataPath.vpn.certificateCN.error.required"),
        v => {
          const CERTIFICATE_CN_PATTERN = /^[\w\-. ]*$/gm;
          if (!CERTIFICATE_CN_PATTERN.test(v)) {
            return this.$t("modules.dataPath.vpn.certificateCN.error.invalid");
          }
          return true;
        }
      ],
      privateProfileRules: [
        v => {
          if (!v) {
            return this.$t("validation.required", [
              this.$t("modules.dataPath.vpnProfile")
            ]);
          }
          return true;
        },
        v => {
          if (v && this.validatePrivateProfiles(v)) {
            return this.$t("error.notAllowedPrivateProfileError");
          }
          return true;
        }
      ]
    };
  },
  methods: {
    openDialog() {
      this.dialog = true;
    },
    close() {
      this.$store.commit("dataPath/clearVpnForm");
      this.dialog = false;
      this.$refs.generateVpnCertForm.resetValidation();
    },
    generateVpnCertificate() {
      if (this.$refs.generateVpnCertForm.validate()) {
        this.loading = true;
        const vpnProfileId =
          this.profileId === "no_vpn" ? null : this.profileId;
        this.$store
          .dispatch("dataPath/generateVpnCert", {
            cn_family: "",
            cert_type: this.certificationType,
            cn: this.certificateCN,
            profile_id: vpnProfileId
          })
          .then(response => {
            const content = response.data;
            const fileName = getFileNameFromResponseHeader(response);
            const contentType = response.headers["content-type"];
            this.downloadBlobFile(content, fileName, contentType);
          })
          .then(() => {
            this.$refs.generateVpnCertForm.resetValidation();
          })
          .catch(handleError)
          .finally(() => {
            this.loading = false;
          });
      }
    },
    downloadBlobFile: util.downloadBlobFile,
    validatePrivateProfiles(v) {
      let profiles = this.vpnProfiles.filter(
        element => element.id === v && element.is_private
      );
      if (profiles.length > 0) {
        return true;
      } else {
        return false;
      }
    }
  },

  created() {
    this.$store.commit("snackbar/toggleMessage", false);
  },
  computed: {
    dialog: {
      get() {
        return this.$store.state.dataPath.vpn.dialog;
      },
      set(val) {
        this.$store.dispatch("dataPath/toggleVpnDialog", val);
      }
    },
    certificationType: {
      get() {
        return this.$store.state.dataPath.vpn.certificationType;
      },
      set(val) {
        this.$store.commit("dataPath/updateCertificationType", val);
      }
    },

    certificateCN: {
      get() {
        return this.$store.state.dataPath.vpn.certificateCN;
      },
      set(val) {
        this.$store.commit("dataPath/updateCertificateCN", val);
      }
    },

    profileId: {
      get() {
        return this.$store.state.dataPath.vpn.profileId;
      },
      set(val) {
        this.$store.commit("dataPath/updateProfileId", val);
      }
    },
    vpnProfilesWithNoVPN() {
      let vpn_profiles = this.$store.state.dataPath.vpnProfile.vpnProfiles;
      let customList = [{ id: "no_vpn", name: "No VPN/Custom" }];
      let profiles = [...customList, ...vpn_profiles];
      return profiles;
    },
    vpnProfiles() {
      return this.$store.state.dataPath.vpnProfile.vpnProfiles;
    }
  }
};
</script>

<template>
  <div class="d-inline" id="generate-vpn-cert__container">
    <v-btn
      color="primary"
      @click="openDialog"
      :disabled="!environment_manager"
      id="generate-vpn-cert__open-dialog"
    >
      {{ $t("modules.dataPath.vpn.generateVpnCertifications") }}
    </v-btn>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="500"
      id="generate-vpn-cert__dialog"
    >
      <v-card id="generate-vpn-cert__form-card">
        <v-form
          v-model="valid"
          ref="generateVpnCertForm"
          @submit.prevent="generateVpnCertificate()"
          id="generate-vpn-cert__form"
        >
          <v-card-title id="generate-vpn-cert_form-title">
            {{ $t("modules.dataPath.vpn.generateVpnCertifications") }}
          </v-card-title>
          <v-card-text>
            <v-container grid-list-md id="generate-vpn-cert__form-container">
              <v-select
                :items="certificationTypes"
                :label="$t('modules.dataPath.vpn.certificationType.label')"
                v-model="certificationType"
                id="generate-vpn-cert_form-cert-type"
              ></v-select>
              <v-text-field
                :label="$t('modules.dataPath.vpn.certificateCN.label')"
                v-model="certificateCN"
                :rules="certCNRules"
                id="generate-vpn-cert__form-cert-cn"
              ></v-text-field>
              <v-select
                v-if="certificationType === 1"
                v-model="profileId"
                :items="vpnProfilesWithNoVPN"
                :label="$t('modules.dataPath.vpnProfile')"
                id="generate-vpn-cert__form-profile-id"
                item-text="name"
                item-value="id"
                clearable
                :rules="privateProfileRules"
              >
              </v-select>
            </v-container>
          </v-card-text>
          <v-card-actions id="generate-vpn-cert__form-actions">
            <v-spacer></v-spacer>
            <v-btn
              color="secondary"
              elevation="1"
              @click="close"
              id="generate-vpn-cert__form-cancel"
            >
              {{ $t("actions.cancel") }}
            </v-btn>
            <v-btn
              color="secondary"
              type="submit"
              :loading="loading"
              id="generate-vpn-cert__form-generate"
            >
              {{ $t("actions.generate") }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>
